import { getToolName, isReasoningUIPart, isTextUIPart, isToolUIPart } from 'ai';
import type { DynamicToolUIPart, ToolUIPart } from 'ai';

import { getToolActivityText, getToolDoneText } from './toolActivityCopy';
import { getWebActivityLabel } from './webSources';
import type { MessagePart } from './types';

export type ReasoningStep = { kind: 'reasoning'; key: string; text: string };
export type ToolMessagePart = ToolUIPart | DynamicToolUIPart;
export type ToolStep = { kind: 'tool'; key: string; toolName: string; parts: ToolMessagePart[] };
export type TextStep = { kind: 'text'; key: string; parts: MessagePart[] };
export type TimelineStep = ReasoningStep | ToolStep | TextStep;

/**
 * Orders an assistant message into the steps a physician sees, in the order they streamed.
 * Back-to-back parts of the same kind (or repeated calls to one tool) read as a single step.
 */
export function buildMessageTimeline(parts: MessagePart[]): TimelineStep[] {
  const steps: TimelineStep[] = [];

  parts.forEach((part, index) => {
    const last = steps.at(-1);
    const key = `step-${index}`;

    if (isReasoningUIPart(part)) {
      if (!part.text.trim()) {
        return;
      }
      if (last?.kind === 'reasoning') {
        last.text = `${last.text}\n\n${part.text}`;
      } else {
        steps.push({ kind: 'reasoning', key, text: part.text });
      }
    } else if (isToolUIPart(part)) {
      const toolName = getToolName(part);
      if (last?.kind === 'tool' && last.toolName === toolName) {
        last.parts.push(part);
      } else {
        steps.push({ kind: 'tool', key, toolName, parts: [part] });
      }
    } else if (isTextUIPart(part)) {
      if (last?.kind === 'text') {
        last.parts.push(part);
      } else if (part.text.trim()) {
        steps.push({ kind: 'text', key, parts: [part] });
      }
    }
  });

  return steps;
}

function isToolRunning(part: ToolMessagePart): boolean {
  return part.state === 'input-streaming' || part.state === 'input-available';
}

function didToolSucceed(part: ToolMessagePart): boolean {
  if (part.state !== 'output-available') {
    return false;
  }
  // Native web search reports its provider status inside an available output,
  // including failed searches. The SDK state alone is not a success signal.
  if (getToolName(part) === 'web_search' && part.providerExecuted) {
    const output = part.output;
    return (
      typeof output === 'object' &&
      output !== null &&
      'status' in output &&
      output.status === 'completed'
    );
  }
  return true;
}

export type ToolStepActivity = { label: string; isRunning: boolean; parts: ToolMessagePart[] };

/** Project activity for display without discarding failures from the transcript or logs. */
export function getToolStepActivity(step: ToolStep, isLive: boolean): ToolStepActivity | null {
  const visibleParts = step.parts.filter(
    (part) => didToolSucceed(part) || (isLive && isToolRunning(part))
  );
  if (visibleParts.length === 0) {
    return null;
  }

  const isRunning = visibleParts.some(isToolRunning);
  const generic = isRunning ? getToolActivityText(step.toolName) : getToolDoneText(step.toolName);
  const fallback =
    !isRunning && visibleParts.length > 1 ? `${generic} ${visibleParts.length} times` : generic;
  return {
    isRunning,
    parts: visibleParts,
    label: getWebActivityLabel(step.toolName, visibleParts, isRunning) ?? fallback,
  };
}

/** The waiting line while nothing new is on screen: before the first step or between tools. */
export function getPendingStatus(steps: TimelineStep[]): string | null {
  const last = steps.at(-1);
  if (!last) {
    return 'Thinking…';
  }
  return last.kind === 'tool' && !last.parts.some(isToolRunning) ? 'Thinking…' : null;
}
